import { useAtom } from "@dbeining/react-atom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { vehiclesService } from "../../services/vehicles/vehicles.service";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, EntityLabels, ListPageTitles, recordLifecycleListConfirmBodies, SectionLabels, showToastSuccess, appRoutes } from "../../utils";
import {
    buildVehicleAdminPagedListActions,
    canMutateVehicles,
    getVehicleAdminActionSuccessMessage,
    type VehicleAdminConfirmKind,
} from "./vehicleAdminListActions";

type ConfirmKind = VehicleAdminConfirmKind | null;

const VehiclesListPage = () => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateVehicles(roleNames);

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmVehicleId, setConfirmVehicleId] = useState<string | null>(null);
    const [confirmVehicleLabel, setConfirmVehicleLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmVehicleId(null);
        setConfirmVehicleLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmVehicleId || !confirmKind) return;
        const id = confirmVehicleId;
        const op =
            confirmKind === "disable"
                ? vehiclesService.disable(id)
                : confirmKind === "enable"
                  ? vehiclesService.enable(id)
                  : confirmKind === "softDelete"
                    ? vehiclesService.softDelete(id)
                    : vehiclesService.restore(id);
        op.then(() => {
            showToastSuccess(getVehicleAdminActionSuccessMessage(confirmKind, confirmVehicleLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(typeof e === "string" ? e : "That action couldn't be completed. Please try again.");
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: { id: string; registration: string }) => {
        setConfirmKind(kind);
        setConfirmVehicleId(item.id);
        setConfirmVehicleLabel(item.registration);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.vehicle,
                confirmVehicleLabel,
                recordLifecycleListConfirmBodies(confirmVehicleLabel)
            ),
        [confirmKind, confirmVehicleLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            {
                member: "registration",
                renderAs: ColumnRender.customRenderer,
                title: "Registration",
                width: 160,
                customRenderer: (item: { registration?: string }) => (
                    <td className="text-start">
                        <span className="font-monospace">{item.registration ?? ""}</span>
                    </td>
                ),
            } as IColumn,
            { member: "vehicleType", renderAs: ColumnRender.text, title: "Type", width: 140 } as IColumn,
            { member: "notes", renderAs: ColumnRender.text, title: "Notes", width: 280 } as IColumn,
            { member: "isEnabled", renderAs: ColumnRender.checkCross, title: "Enabled", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Registration", member: "registrationSearch", type: FilterType.text } as IFilter,
            { title: "Type", member: "vehicleTypeSearch", type: FilterType.text } as IFilter,
            { title: "Notes", member: "notesSearch", type: FilterType.text } as IFilter,
            {
                title: "Enabled",
                member: "isEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        []
    );

    const itemActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(`/vehicles/${item.id}`),
                primary: true,
            } as IPagedListAction,
            ...buildVehicleAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ];
    }, [canMutate, navigate]);

    const headActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: addEntityActionLabel(EntityLabels.vehicle),
                icon: faPlus,
                onClick: () => navigate("/vehicles/new"),
                primary: true,
            } as IPagedListAction,
        ];
    }, [canMutate, navigate]);

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.vehicles}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: SectionLabels.movementParties, to: appRoutes.transportParties },
                            { text: ListPageTitles.vehicles },
                        ]}
                    />
                    <h1>{ListPageTitles.vehicles}</h1>
                    <p className="text-muted">
                        Vehicles used at your sites. The same registration can appear on multiple jobs; listing a
                        vehicle here does not mean you own it.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Vehicle actions"
                        showDeletedRowIndicator
                        defaultSortBy="Registration"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/vehicles/all"
                        rowFormatter={() => null}
                        persistanceKey="VehiclesListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { VehiclesListPage };
